import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { useAuthStore } from "./stores/authStore";
import { useEffect, lazy, Suspense } from "react";
import Layout from "./components/Layout";
import ProtectedRoute from "./components/ProtectedRoute";
import FeedbackWidget from "./components/FeedbackWidget";
import DevCoachStatePanel from "./components/DevCoachStatePanel";

// Auth pages - load immediately (small, needed early)
import LoginPage from "./pages/landing/LoginPage";
import SignupPage from "./pages/landing/SignupPage";
import ForgotPasswordPage from "./pages/landing/ForgotPasswordPage";
import ResetPasswordPage from "./pages/landing/ResetPasswordPage";
import VerifyEmailPage from "./pages/landing/VerifyEmailPage";
import TermsPage from "./pages/landing/TermsPage";

// Lazy load heavy/less-used pages
const LandingPage = lazy(() => import("./pages/landing/LandingPage"));
const TrainerHomePage = lazy(() => import("./pages/TrainerHomePage"));
const PublicHomePage = lazy(() => import("./pages/landing/PublicHomePage"));
const PublicTrainerLandingPage = lazy(
  () => import("./pages/landing/PublicTrainerLandingPage"),
);
const PublicSoloLandingPage = lazy(
  () => import("./pages/landing/PublicSoloLandingPage"),
);
const ExercisesPage = lazy(() => import("./pages/ExercisesPage"));
const VideoUploadPage = lazy(
  () => import("./pages/exercise/ExerciseCreatePage"),
);
const ExercisePage = lazy(() => import("./pages/ExercisePage"));
const ProgramsPage = lazy(() => import("./pages/ProgramsPage"));
const ProgramEditorPage = lazy(() => import("./pages/ProgramEditorPage"));
const ProgramViewerPage = lazy(() => import("./pages/ProgramViewerPage"));
const SessionsPage = lazy(() => import("./pages/SessionsPage"));
const ProgramCompletionsPage = lazy(() => import("./pages/SessionDetailsPage"));
const SessionVideoPlayerPage = lazy(
  () => import("./pages/SessionVideoPlayerPage"),
);
const CustomersPage = lazy(() => import("./pages/CustomersPage"));
const TrainersPage = lazy(() => import("./pages/UsersTrainersPage"));
const SharedAccessPage = lazy(() => import("./pages/SharedAccessPage"));
const SharedProgramViewerPage = lazy(
  () => import("./pages/SharedProgramViewerPage"),
);
const InvitationLandingPage = lazy(
  () => import("./pages/landing/InvitationLandingPage"),
);
const UserSettingsPage = lazy(() => import("./pages/UserSettingsPage"));
const MainDeviceMirrorPage = lazy(() => import("./pages/MirrorCameraPage"));
const ConnectedDeviceMirrorPage = lazy(
  () => import("./pages/MirrorStreamPage"),
);
const AiCoachWidgetTestPage = lazy(
  () => import("./pages/AiCoachWidgetTestPage"),
);
const HelpPage = lazy(() => import("./pages/landing/HelpPage"));
const GoalGraphPage = lazy(() => import("./components/targets/TargetGraph"));
const AssessTargetPage = lazy(() => import("./pages/TargetAssessmentPage"));
const UserTargetsEditorPage = lazy(
  () => import("./pages/UserTargetsEditorPage"),
);

const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="text-text-secondary">Loading...</div>
  </div>
);

function App() {
  const { user, loading, checkSession } = useAuthStore();
  const navigate = useNavigate();

  // Check session on app mount
  useEffect(() => {
    checkSession();
  }, [checkSession]);

  // Global handler for 401 unauthorized errors
  useEffect(() => {
    const handleUnauthorized = () => {
      console.log("Global: Received auth:unauthorized event");
      useAuthStore.getState().handleUnauthorized();
      navigate("/login");
    };

    window.addEventListener("auth:unauthorized", handleUnauthorized);
    return () =>
      window.removeEventListener("auth:unauthorized", handleUnauthorized);
  }, [navigate]);

  return (
    <div className="text-text-primary max-h-dvh max-w-dvw overflow-auto bg-bg-secondary">
      {/* Feedback Widget - Only in development */}
      {import.meta.env.DEV && <FeedbackWidget />}
      {import.meta.env.DEV && <DevCoachStatePanel />}

      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public Home Page */}
          <Route path="/welcome" element={<PublicHomePage />} />

          {/* Public Landing Pages */}
          <Route path="/trainer" element={<PublicTrainerLandingPage />} />
          <Route path="/solo" element={<PublicSoloLandingPage />} />

          {/* Public Routes - No auth required */}
          <Route path="/account/:code" element={<InvitationLandingPage />} />
          <Route path="/access/:token" element={<SharedAccessPage />} />
          <Route
            path="/access/:token/program/:programId"
            element={<SharedProgramViewerPage />}
          />

          {/* WebRTC Streaming Routes - No auth required for easy local network access */}
          <Route path="/mirror/main" element={<MainDeviceMirrorPage />} />
          <Route
            path="/mirror/connect"
            element={<ConnectedDeviceMirrorPage />}
          />

          {/* Legacy route aliases */}
          <Route
            path="/stream/broadcast"
            element={<Navigate to="/mirror/main" replace />}
          />
          <Route
            path="/stream/view"
            element={<Navigate to="/mirror/connect" replace />}
          />
          <Route
            path="/stream/simple-broadcast"
            element={<Navigate to="/mirror/main" replace />}
          />
          <Route
            path="/stream/simple-view"
            element={<Navigate to="/mirror/connect" replace />}
          />

          <Route
            path="/login"
            element={!user ? <LoginPage /> : <Navigate to="/" />}
          />
          <Route
            path="/signup"
            element={!user ? <SignupPage /> : <Navigate to="/" />}
          />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/terms" element={<TermsPage />} />

          <Route element={<Layout />}>
            <Route
              path="/"
              element={
                loading ? (
                  <PageLoader />
                ) : user ? (
                  <ProtectedRoute>
                    <LandingPage />
                  </ProtectedRoute>
                ) : (
                  <Navigate to="/welcome" />
                )
              }
            />
            <Route
              path="/home"
              element={
                <ProtectedRoute>
                  <TrainerHomePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/videos"
              element={
                <ProtectedRoute>
                  <ExercisesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/upload"
              element={
                <ProtectedRoute>
                  <VideoUploadPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/videos/:id"
              element={
                <ProtectedRoute>
                  <ExercisePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/session-video/:videoId"
              element={
                <ProtectedRoute>
                  <SessionVideoPlayerPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/programs"
              element={
                <ProtectedRoute>
                  <ProgramsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/programs/new"
              element={
                <ProtectedRoute>
                  <ProgramEditorPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/programs/:id/edit"
              element={
                <ProtectedRoute>
                  <ProgramEditorPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/customers"
              element={
                <ProtectedRoute>
                  <CustomersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/trainers"
              element={
                <ProtectedRoute>
                  <TrainersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <UserSettingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sessions"
              element={
                <ProtectedRoute>
                  <SessionsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/goals"
              element={
                <ProtectedRoute>
                  <GoalGraphPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/goals/edit"
              element={
                <ProtectedRoute>
                  <UserTargetsEditorPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/targets/:id/assess"
              element={
                <ProtectedRoute>
                  <AssessTargetPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/ai-coach/widget-test"
              element={<AiCoachWidgetTestPage />}
            />
            <Route path="/help" element={<HelpPage />} />
          </Route>
          <Route
            path="/programs/:id"
            element={
              <ProtectedRoute>
                <ProgramViewerPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/programs/:id/completions"
            element={
              <ProtectedRoute>
                <ProgramCompletionsPage />
              </ProtectedRoute>
            }
          />
        </Routes>
      </Suspense>
    </div>
  );
}

export default App;
